import { useState, useContext, useEffect } from 'react';
import { useCallback } from 'react';
import { api, mapApiError } from '../../../model/api/api';
import { Identities } from '../../../model/store/providers/Identities';
import Modal from 'react-modal';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { toaster } from '../../../model/toast/toaster';
import { Localization } from '../../../model/store/providers/Localization';
import './LinkAccountModal.scss';
import { LocalSystemConfig } from '../../../model/localSystems';
import { resourcesBaseUri } from '../../../config';

export type Props = {
	isOpen: boolean;
	system: LocalSystemConfig;
	closeModal: () => void;
}

const modalStyles = {
	overlay: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: '#00000066'
	}
};

// Requested by the Modal package. If it's not present, an error is logged.
const appElement = document.getElementById('root');

export const LinkAccountModal = ({ isOpen, system, closeModal }: Props) => {
	const [authenticationError, setAuthenticationError] = useState('');
	const [loginName, setLoginName] = useState('');
	const [passwordValue, setPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [passwordShown, setPasswordShown] = useState(false);
	const dispatch = useContext(Identities.Dispatch);
	const { translations } = useContext(Localization.State);
	const {
		linkAccountModal: {
			title,
			subTitle,
			email,
			password,
			submitBtn,
			successToast,
			errorMessageIconAlt,
			hideShowPasswordIconAlt
		},
		apiErrors
	} = translations;

	useEffect(() => {
		setAuthenticationError('');
		setLoginName('');
		setPassword('');
	}, [isOpen])

	const onSubmit = useCallback(async () => {
		try {
			setSubmitting(true);

			const { localId } = await api.linkIdentity(system.id, { loginName, password: passwordValue });

			dispatch({
				type: 'linkIdentity',
				systemId: system.id,
				identity: {
					id: localId
				}
			});

			setAuthenticationError('');
			toaster.success(`${localId} ${successToast}`);

			closeModal();
		}
		catch(error: any) {
			setAuthenticationError(mapApiError(error, apiErrors));
		}
		finally {
			setSubmitting(false);
		}
	}, [dispatch, loginName, passwordValue, system, closeModal, successToast, apiErrors]);

	const onLoginNameChange = useCallback(({ target }: { target: HTMLInputElement }) => {
		setLoginName(target.value);
		setAuthenticationError('');
	}, []);

	const onPasswordChange = useCallback(({ target }: { target: HTMLInputElement }) => {
		setPassword(target.value);
		setAuthenticationError('');
	}, []);

	const validateForm = useCallback(() => {
		return loginName.length > 0 && passwordValue.length > 0;
	}, [loginName, passwordValue]);

	const togglePasswordVisibility = useCallback(() => {
		setPasswordShown(!passwordShown);
	}, [passwordShown]);

	return <>
		<LoadingIndicator show={submitting} blockInteractions />
		<Modal
			className="link-account-modal"
			isOpen={isOpen}
			style={modalStyles}
			onRequestClose={closeModal}
			appElement={appElement!}
		>
			<span className="link-account-modal__header">
				{title}
				<span className="link-account-modal__close" onClick={closeModal}>{ '\u2715' }</span>
			</span>
			<div className="link-account-modal__header-bar" />
			<span className="link-account-modal__body">
				<div className="link-account-modal__image-container">
					<div className={authenticationError ? "link-account-modal__fail-message" : "none"}>
						<img
							className="link-account-modal__fail-message__img"
							src={`${resourcesBaseUri}/error-icon.svg`}
							alt={errorMessageIconAlt}
						/>
						<span className="link-account-modal__fail-message__text">{authenticationError}</span>
					</div>
					<img
						className={"link-account-modal__logo-" + system.id}
						src={system.icon}
						alt={system.id}
					/>
				</div>
				<div className="link-account-modal__form">
					<div className="link-account-modal__credentials">{subTitle[0]} {system.name} {subTitle[1]}</div>
					<span className="link-account-modal__input-heading">{email.title}</span>
					<input
						className={authenticationError ? "link-account-modal__input-fail" : "link-account-modal__input"}
						placeholder={email.placeholder}
						type="text"
						onChange={onLoginNameChange}
					/>
					<span className="link-account-modal__input-heading">{password.title}</span>
					<span className="link-account-modal__input-container">
						<input
							className={authenticationError ? "link-account-modal__input-fail" : "link-account-modal__input"}
							placeholder={password.placeholder}
							type={passwordShown ? "text" : "password"}
							onChange={onPasswordChange}
						/>
						<img
							onClick={togglePasswordVisibility}
							className={"link-account-modal__password-icon--visible"}
							src={passwordShown ?
								`${resourcesBaseUri}/eye.svg` :
								`${resourcesBaseUri}/eye-slash.svg`
							}
							alt={hideShowPasswordIconAlt}
							tabIndex={0}
						/>
					</span>
					<button
						className="link-account-modal__submit-btn"
						onClick={onSubmit}
						disabled={!validateForm()}
					>{submitBtn}</button>
				</div>
			</span>
		</Modal>
	</>;
}